import { component } from 'picoapp';
import Drawer from 'utils/drawer';
import { getCart } from 'utils/cart';
import { clear, repaint } from 'utils/node';
import debounce from 'lodash/debounce';
import transitionEnd from 'utils/transition-end';

export default component((node, ctx) => {
  const drawer = Drawer(node);
  const cartDrawerTriggers = [
    ...document.querySelectorAll<HTMLElement>('[data-trigger="cart-drawer"]'),
  ];
  const cartForm = node.querySelector<HTMLFormElement>('[data-cart-form]');
  const counts = [
    ...document.querySelectorAll<HTMLElement>('[data-cart-count]'),
  ];
  const counsellingInputs = [
    ...document.querySelectorAll<HTMLInputElement>('[data-counselling-input]'),
  ];

  const DRAWER_ACTIVE_CLASS = 'drawer--active';
  const BUTTON_SUCCESS_CLASS = 'button--success';

  const updateSelectors = {
    inner: '[data-inner]',
  };

  if (counsellingInputs) {
    counsellingInputs.forEach((input) => {
      input.addEventListener('change', () => {
        counsellingInputs
          .filter((item) => item !== input)
          .forEach((item) => (item.checked = input.checked));
      });
    });
  }

  const updateCounts = ({ item_count: itemCount }: { item_count: string }) => {
    counts.forEach((count) => {
      count.textContent = parseInt(itemCount, 10).toString();
    });
  };

  const getHTML = async () => {
    const response = await fetch(`/?section_id=${node.id}`);
    const html = await response.text();

    const temp = document.createElement('div');
    temp.innerHTML = html;

    return temp;
  };

  const loading = () => {
    const list = node.querySelector('[data-items]');
    if (list) list.classList.add('is-loading');
  };

  const unloading = () => {
    const list = node.querySelector('[data-items]');
    if (list) list.classList.remove('is-loading');
  };

  // window.addEventListener('resize', debounce(() => setBestSource(), 500));

  node.addEventListener('click', (event: Event) => {
    const target = event.target as HTMLElement;

    if (target.hasAttribute('data-change')) {
      event.preventDefault();

      const modifier = parseInt(target.dataset.modifier, 10);
      const cartItem = target.closest('[data-cart-item]');
      const quantity = cartItem.querySelector('[data-quantity]');
      const currentQuantity = parseInt(quantity.textContent, 10);

      quantity.textContent = `${Math.max(currentQuantity + modifier, 0)}`;
    }
  });

  node.addEventListener(
    'click',
    debounce(async (event: Event) => {
      const target = event.target as HTMLLinkElement;

      if (target.hasAttribute('data-change')) {
        const cartItem = target.closest('[data-cart-item]');
        const quantity = cartItem.querySelector('[data-quantity]');
        const currentQuantity = parseInt(quantity.textContent, 10);
        const url = new URL(target.href ?? target.dataset.href);

        url.searchParams.set('quantity', currentQuantity.toString());
        url.pathname = `${url.pathname}.js`;

        loading();
        await fetch(url.href);
        const data = await getCart();

        ctx.emit('cart:update', { cart: data });
      }
    }, 300)
  );

  ctx.on('cart:update', async ({ cart, openDrawer }) => {
    const response = await getHTML();
    window.theme.cart = cart;

    Object.values(updateSelectors).forEach((item) => {
      const responseElement = response.querySelector(item);
      const element = node.querySelector(item);

      clear(element);
      if (responseElement) {
        element.insertAdjacentHTML('beforeend', responseElement.innerHTML);
      }
    });

    if (openDrawer) ctx.emit('cart:open');
    updateCounts(cart);

    document.dispatchEvent(new Event('cart:updated'));
  });

  document.addEventListener('cart:product:added', () => {
    getCart().then((cart) => {
      console.log('cart:product:added', cart);

      // Clear existing product items in session storage
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('product-')) {
          sessionStorage.removeItem(key);
        }
      });

      // Store each cart item as an individual session storage item
      cart.items.forEach((item, index) => {
        const productInfo = {
          title: item.title,
          quantity: item.quantity,
          url: item.url,
        };
        sessionStorage.setItem(
          `product-${index + 1}`,
          JSON.stringify(productInfo)
        );
      });

      ctx.emit('cart:update', { cart, openDrawer: false });
    });
  });

  document.addEventListener('terms:accept', () => {
    const termsInput =
      node.querySelector<HTMLInputElement>('input[name=terms]');
    const submit: HTMLButtonElement = node.querySelector(
      'button[name="checkout"]'
    );

    termsInput.checked = true;
    submit.click();
  });

  const initCartDrawer = () => {
    if (!drawer || !cartDrawerTriggers.length) return;

    cartDrawerTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        drawer.toggle();
      });
    });

    ctx.on('cart:open', () => drawer.open());
  };

  const initCartForm = () => {
    cartForm.addEventListener('submit', (e) => {
      const termsInput =
        node.querySelector<HTMLInputElement>('input[name=terms]');

      if (!termsInput.checked) {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('terms:trigger'));
      }
    });
  };

  const initSendCartForm = () => {
    const sendCartDrawerButtons = Array.from(
      document.querySelectorAll<HTMLButtonElement>(
        '[data-send-cart-drawer-button]'
      )
    );
    const sendCartDrawer = node.querySelector<HTMLElement>(
      '[data-send-cart-drawer]'
    );
    const sendCartDrawerCloseButton = node.querySelector(
      '[data-send-cart-drawer-close-button]'
    );
    const sendCartForm = node.querySelector<HTMLFormElement>(
      '[data-send-cart-form]'
    );
    const sendCartSubmit = sendCartForm.querySelector('[type=submit]');

    sendCartDrawerButtons.forEach((element) => {
      element.addEventListener('click', () => {
        drawer.open();
        sendCartDrawer.hidden = false;
        repaint(sendCartDrawer);
        sendCartDrawer.classList.add(DRAWER_ACTIVE_CLASS);
      });
    });

    if (sendCartDrawerCloseButton) {
      sendCartDrawerCloseButton.addEventListener('click', () => {
        sendCartDrawer.classList.remove(DRAWER_ACTIVE_CLASS);

        transitionEnd(sendCartDrawer, () => {
          if (!sendCartDrawer.classList.contains(DRAWER_ACTIVE_CLASS)) {
            sendCartDrawer.hidden = true;
          }
        });
      });
    }

    sendCartForm.addEventListener('submit', async (event) => {
      event.preventDefault();

      const requiredInputs = Array.from(
        sendCartForm.querySelectorAll<HTMLInputElement>('#required')
      );

      let formIsValid = true;

      requiredInputs.forEach((input) => {
        if (!input.value) {
          formIsValid = false;
          input.classList.add('placeholder:text-[#d23836]');
        } else {
          input.classList.remove('placeholder:text-[#d23836]');
        }
      });

      if (formIsValid) {
        const sortedKeys = Object.keys(sessionStorage)
          .filter((key) => key.startsWith('product-'))
          .sort((a, b) => {
            const aNum = parseInt(a.split('-')[1], 10);
            const bNum = parseInt(b.split('-')[1], 10);
            return aNum - bNum;
          });

        // Iterate over the sorted keys and create hidden inputs
        sortedKeys.forEach((key) => {
          const productInfo = JSON.parse(sessionStorage.getItem(key) || '{}');
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `contact[${key}]`;
          input.value = `
            ${productInfo.title}: ${productInfo.quantity}
            ${window.theme.storeUrl}${productInfo.url}
          `;
          sendCartForm.appendChild(input);
        });

        const entries = Array.from(new FormData(sendCartForm).entries());
        const urlString = entries
          .map(([name, value]) => `${name}=${value}`)
          .join('&');

        const response = await fetch(sendCartForm.action, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
          body: urlString,
        });

        if (response.status !== 200) {
          // eslint-disable-next-line no-alert
          alert(theme.strings.cart.sendCartToSeller.error);
        } else {
          const sendCartSubmitHtml = sendCartSubmit.innerHTML;
          sendCartSubmit.classList.add(BUTTON_SUCCESS_CLASS);
          sendCartSubmit.textContent =
            theme.strings.cart.sendCartToSeller.success;

          setTimeout(() => {
            sendCartSubmit.classList.remove(BUTTON_SUCCESS_CLASS);
            clear(sendCartSubmit);
            sendCartSubmit.insertAdjacentHTML('beforeend', sendCartSubmitHtml);

            sendCartDrawer.classList.remove(DRAWER_ACTIVE_CLASS);

            transitionEnd(sendCartDrawer, () => {
              if (!sendCartDrawer.classList.contains(DRAWER_ACTIVE_CLASS)) {
                sendCartDrawer.hidden = true;
              }
            });

            drawer.close();
          }, 2000);
        }
      }
    });
  };

  document.addEventListener('cart:updated', () => {
    unloading();
    initSendCartForm();
  });

  initCartDrawer();
  initCartForm();
  initSendCartForm();
});
